import apiCall from '@/store/apiCall';
import URLS from '@/store/urls';
import notificationWithIcon from '@/utils/notification';

const actions = {
  // Получение списка конфигураций для компании
  async getApiConfigs({ commit }, companyId) {
    commit('GET_API_CONFIGS_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.apiConfig}/company/${companyId}`,
        isToken: true,
      });

      commit('GET_API_CONFIGS_SUCCESS', { data: data.data || data, total: data.total || 0 });
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load API configurations';
      commit('GET_API_CONFIGS_ERROR');
      notificationWithIcon('error', message);
    }
  },

  // Получение одной конфигурации
  async getApiConfig({ commit }, id) {
    commit('GET_API_CONFIG_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.apiConfig}/${id}`,
        isToken: true,
      });

      commit('GET_API_CONFIG_SUCCESS', data);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load API configuration';
      commit('GET_API_CONFIG_ERROR');
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Создание конфигурации
  async createApiConfig({ commit, dispatch }, { companyId, values }) {
    commit('CREATE_API_CONFIG_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'POST',
        url: URLS.apiConfig,
        body: { ...values, companyId },
        isToken: true,
      });

      commit('CREATE_API_CONFIG_SUCCESS', data);
      notificationWithIcon('success', 'API configuration created successfully');

      // Обновляем список конфигураций
      await dispatch('getApiConfigs', companyId);

      return data;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create API configuration';
      commit('CREATE_API_CONFIG_ERROR');
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Обновление конфигурации
  async updateApiConfig({ commit, dispatch }, { id, companyId, values }) {
    commit('UPDATE_API_CONFIG_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'PUT',
        url: `${URLS.apiConfig}/${id}`,
        body: values,
        isToken: true,
      });
      commit('UPDATE_API_CONFIG_SUCCESS', data);
      notificationWithIcon('success', 'API configuration updated successfully');

      // Обновляем список конфигураций
      await dispatch('getApiConfigs', companyId);

      return data;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update API configuration';
      commit('UPDATE_API_CONFIG_ERROR');
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Удаление конфигурации
  async deleteApiConfig({ commit, dispatch }, { id, companyId }) {
    commit('DELETE_API_CONFIG_REQUEST');
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.apiConfig}/${id}`,
        isToken: true,
      });

      commit('DELETE_API_CONFIG_SUCCESS', id);
      notificationWithIcon('success', 'API configuration deleted successfully');

      // Обновляем список конфигураций
      await dispatch('getApiConfigs', companyId);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to delete API configuration';
      commit('DELETE_API_CONFIG_ERROR');
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Тестирование подключения
  async testConnection({ commit }, id) {
    commit('TEST_CONNECTION_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.apiConfig}/${id}/test`,
        isToken: true,
      });

      commit('TEST_CONNECTION_SUCCESS', data);

      if (data.success) {
        notificationWithIcon('success', 'Connection test successful');
      } else {
        notificationWithIcon('warning', data.message || 'Connection test failed');
      }

      return data;
    } catch (err) {
      const message = err.response?.data?.message || 'Connection test failed';
      commit('TEST_CONNECTION_ERROR', message);
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Очистка результата теста
  clearTestResult({ commit }) {
    commit('CLEAR_TEST_RESULT');
  },

  // Предпросмотр данных
  async getPreviewData({ commit }, id) {
    commit('GET_PREVIEW_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.apiConfig}/${id}/preview`,
        isToken: true,
      });

      commit('GET_PREVIEW_SUCCESS', data);
      return data;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load preview data';
      commit('GET_PREVIEW_ERROR');
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Очистка предпросмотра
  clearPreview({ commit }) {
    commit('CLEAR_PREVIEW');
  },

  // Получение debug информации
  async getDebugInfo({ commit }, id) {
    commit('GET_DEBUG_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.apiConfig}/${id}/debug`,
        isToken: true,
      });

      commit('GET_DEBUG_SUCCESS', data);
      return data;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load debug information';
      commit('GET_DEBUG_ERROR');
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Очистка debug информации
  clearDebug({ commit }) {
    commit('CLEAR_DEBUG');
  },

  // Получение логов синхронизации
  async getSyncLogs({ commit }, { companyId, limit = 10, offset = 0 }) {
    commit('GET_SYNC_LOGS_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.apiConfig}/company/${companyId}/sync-logs?limit=${limit}&offset=${offset}`,
        isToken: true,
      });

      commit('GET_SYNC_LOGS_SUCCESS', { data: data.data || data, total: data.total || 0 });
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load sync logs';
      commit('GET_SYNC_LOGS_ERROR');
      notificationWithIcon('error', message);
    }
  },

  // Получение последней синхронизации
  async getLastSync({ commit }, companyId) {
    commit('GET_LAST_SYNC_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.apiConfig}/company/${companyId}/last-sync`,
        isToken: true,
      });

      commit('GET_LAST_SYNC_SUCCESS', data);
    } catch (err) {
      commit('GET_LAST_SYNC_ERROR');
      // Don't show notification for this optional request
    }
  },

  // Запуск синхронизации
  async startSync({ commit, dispatch }, { companyId, configId }) {
    commit('START_SYNC_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'POST',
        url: `${URLS.apiConfig}/company/${companyId}/sync`,
        body: configId ? { configId } : {},
        isToken: true,
      });

      commit('START_SYNC_SUCCESS', data);
      notificationWithIcon('info', 'Synchronization started');

      // Начинаем отслеживание статуса
      dispatch('startSyncStatusPolling', { companyId, configId });

      return data;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to start synchronization';
      commit('START_SYNC_ERROR', message);
      notificationWithIcon('error', message);
      throw err;
    }
  },

  // Запуск автообновления статуса синхронизации
  startSyncStatusPolling({ commit, dispatch, state }, { companyId, configId }) {
    // Очищаем предыдущий интервал если есть
    commit('CLEAR_SYNC_STATUS_INTERVAL');

    // Функция проверки статуса
    const checkStatus = async () => {
      try {
        // Получаем обновленные конфигурации
        await dispatch('getApiConfigs', companyId);

        // Проверяем есть ли активные синхронизации
        const config = state.apiConfigs.find((c) => c.id === configId);
        if (!config || config.lastSyncStatus !== 'in_progress') {
          // Синхронизация завершена, останавливаем polling
          commit('CLEAR_SYNC_STATUS_INTERVAL');

          // Обновляем логи
          await dispatch('getSyncLogs', { companyId, limit: 10, offset: 0 });

          // Показываем уведомление о завершении
          if (config) {
            if (config.lastSyncStatus === 'success') {
              const msg = `Synchronization completed. Users synced: ${config.lastSyncUsersCount || 0}`;
              notificationWithIcon('success', msg);
            } else if (config.lastSyncStatus === 'failed') {
              const errorMsg = `Synchronization failed: ${config.lastSyncError || 'Unknown error'}`;
              notificationWithIcon('error', errorMsg);
            }
          }
        }
      } catch (err) {
        console.error('Error checking sync status:', err);
      }
    };

    // Проверяем сразу
    checkStatus();

    // Устанавливаем интервал проверки каждые 5 секунд
    const interval = setInterval(checkStatus, 5000);
    commit('SET_SYNC_STATUS_INTERVAL', interval);
  },

  // Остановка автообновления статуса
  stopSyncStatusPolling({ commit }) {
    commit('CLEAR_SYNC_STATUS_INTERVAL');
  },

  // Очистка состояния при размонтировании компонента
  clearState({ commit }) {
    commit('CLEAR_SYNC_STATUS_INTERVAL');
    commit('CLEAR_TEST_RESULT');
    commit('CLEAR_PREVIEW');
    commit('CLEAR_DEBUG');
  },
};

export default actions;
